import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useUser } from '@/hooks/auth/use-user';
import { useEffect } from 'react';

export interface UserProfile {
  firstName: string;
  lastName: string;
  profilePictureUrl: string;
  email: string;
  userRole: string;
  createdAt: string;
  finalpieceStaff: boolean;
}

/**
 * Single source of truth for user profile data
 * Replaces useUnifiedProfileData and eliminates profile data from AI dashboard query
 */
export const useUserProfile = () => {
  const { user } = useUser();
  const queryClient = useQueryClient();
  
  // Listen for profile picture updates and invalidate cache
  useEffect(() => {
    const handleProfileUpdate = (event: CustomEvent) => {
      console.log('[UserProfile] Profile update event received:', event.detail);
      if (user?.id) {
        queryClient.invalidateQueries({ queryKey: ['user-profile', user.id] });
      }
    };
    
    document.addEventListener('profile-picture-updated', handleProfileUpdate as EventListener);
    document.addEventListener('profile-updated', handleProfileUpdate as EventListener);
    
    return () => {
      document.removeEventListener('profile-picture-updated', handleProfileUpdate as EventListener);
      document.removeEventListener('profile-updated', handleProfileUpdate as EventListener);
    };
  }, [user?.id, queryClient]);
  
  const { data: profile, isLoading, error, refetch } = useQuery<UserProfile>({
    queryKey: ['user-profile', user?.id],
    queryFn: async () => {
      if (!user?.id) {
        throw new Error('No authenticated user');
      }
      
      console.log('[UserProfile] Fetching user profile for:', user.id);
      
      const { data, error } = await supabase
        .from('users')
        .select('first_name, last_name, profile_picture_url, email, created_at, finalpiece_staff')
        .eq('id', user.id)
        .single();
        
      if (error) {
        console.warn('[UserProfile] Database error:', error.message);
        // Return fallback data instead of throwing
        return {
          firstName: '',
          lastName: '',
          profilePictureUrl: '',
          email: user?.email || '',
          userRole: 'User',
          createdAt: '',
          finalpieceStaff: false
        };
      }
      
      console.log('[UserProfile] Successfully fetched profile with picture:', data?.profile_picture_url);
      
      return {
        firstName: data?.first_name || '',
        lastName: data?.last_name || '',
        profilePictureUrl: data?.profile_picture_url || '',
        email: data?.email || user.email || '',
        userRole: 'User',
        createdAt: data?.created_at || '',
        finalpieceStaff: data?.finalpiece_staff || false
      };
    },
    enabled: !!user?.id,
    staleTime: 0, // Always fetch fresh data for profile updates
    gcTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: true,
    refetchOnMount: true,
    retry: 2,
    retryDelay: 1000,
  });

  // Provide default empty profile if no data
  const userProfile: UserProfile = profile || {
    firstName: '',
    lastName: '',
    profilePictureUrl: '',
    email: user?.email || '',
    userRole: 'User',
    createdAt: '',
    finalpieceStaff: false
  };

  return { 
    profile: userProfile, 
    isLoading, 
    error,
    refetch
  };
}; 